class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    this.updateFreeShippingMessageFromCart();
    
    setTimeout(() => {
      if (window.CartRecommendationsManager) {
        window.CartRecommendationsManager.loadRecommendationsForLastCartItem();
      }
    }, 100);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.#preventBodyScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.#restoreBodyScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (parsedState.total_price !== undefined) {
      this.updateFreeShippingMessage(parsedState.total_price);
      this.syncCartTotal(parsedState.total_price);
    }


    setTimeout(() => {
      if (window.CartRecommendationsManager) {
        window.CartRecommendationsManager.handleCartUpdate(parsedState);
      }
    }, 500);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateFreeShippingMessage(cartTotal) {
    const freeShippingMessage = document.getElementById('free-shipping-message');
    if (!freeShippingMessage) return;

    const freeShippingThreshold = window.Shopify?.theme?.settings?.free_shipping_threshold || 5000;
    const remainingAmount = freeShippingThreshold - cartTotal;

    if (remainingAmount > 0) {
      const remainingMoney = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(remainingAmount / 100);
      freeShippingMessage.innerHTML = `You are <strong>${remainingMoney}</strong> away from free shipping.`;
    } else {
      freeShippingMessage.textContent = 'Congratulations! You qualify for free shipping.';
    }
  }

  updateFreeShippingMessageFromCart() {
    const totalValueElement = document.querySelector('.totals__total-value');
    if (!totalValueElement) return;

    const totalText = totalValueElement.textContent || totalValueElement.innerText;
    const numericValue = totalText.replace(/[^\d.,]/g, '').replace(',', '');
    const cartTotal = parseFloat(numericValue) * 100;

    if (!isNaN(cartTotal)) {
      this.updateFreeShippingMessage(cartTotal);
      this.syncCartTotal(cartTotal);
    }
  }

  syncCartTotal(cartTotal) {
    const drawerTotal = document.querySelector('.totals__total-value');
    const cartPageTotal = document.querySelector('.custom-cart__total-value');
    
    if (drawerTotal && cartPageTotal) {
      const formattedMoney = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(cartTotal / 100);
      
      const currentCartText = cartPageTotal.textContent;
      const currentDrawerText = drawerTotal.textContent;
      
      if (currentCartText !== currentDrawerText) {
        cartPageTotal.textContent = formattedMoney;
        drawerTotal.textContent = formattedMoney;
      }
    }
  }

  syncCartTotalFromElement(totalElement) {
    const cartPageTotal = document.querySelector('.custom-cart__total-value');
    
    if (cartPageTotal && totalElement) {
      const newTotalText = totalElement.textContent;
      const currentCartText = cartPageTotal.textContent;
      
      if (currentCartText !== newTotalText) {
        cartPageTotal.textContent = newTotalText;
      }
    }
  }

  #preventBodyScroll() {
    this.scrollY = window.scrollY;
    
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
    
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
  }

  #restoreBodyScroll() {
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
    
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
    }
  }

}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
